import { useEffect, useState } from 'react'
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap'
import * as Sentry from '@sentry/react'

import AssessmentForm from '../AssessmentForm'
import RembleLogomark from '../../../assets/images/Remble_Logomark-Green.png'
import {
  requestNotificationAuthorization,
  toggleTabBar
} from 'components/common/helpers/Mobile'
import {
  EVENT_ACTION_COMPLETE,
  EVENT_ACTION_START,
  EVENT_CATEGORY_ASSESSMENT,
  saveUserEvent
} from 'components/common/api/UserEvents'
import {
  getUserAssessmentFormData,
  saveUserAssessmentData
} from 'components/common/api/Users'
import { navigateTo } from 'components/common/navigation'
import { ROUTE_ASSESSMENT_COMPLETE } from 'components/common/helpers/Paths'
import { useHistory } from 'react-router-dom'

/**
 * User Assessment component: used to render the user assessment which customizes the user experience
 */
export default function UserAssessment({ handleComplete }) {
  const history = useHistory()

  // State
  const [assessmentData, setAssessmentData] = useState()
  const [showForm, setShowForm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState()

  /**
   * Callback function to be called when the assessment is complete
   * @param {Object} formData - completed form data
   */
  const assessmentCompleteCallback = async formData => {
    // Check to see if the user has requested daily reminders
    if (formData['daily-reminders'] === 'on') {
      // Request notification authorization
      requestNotificationAuthorization()
    }

    // Save the user's assessment data
    const saved = await saveUserAssessmentData(formData)
    const saveError = saved.error || saved.detail

    // If we had an error, set the error state
    if (saveError) {
      Sentry.captureException(saveError)
      console.error('Error saving assessment data', saveError)
      setError(saveError)
      return
    }

    // Log the event
    saveUserEvent(EVENT_CATEGORY_ASSESSMENT, EVENT_ACTION_COMPLETE)

    // Handle complete, if provided
    if (handleComplete) return handleComplete()

    navigateTo(ROUTE_ASSESSMENT_COMPLETE, history)
  }

  useEffect(() => {
    // Return early if we already have the assessment data
    if (assessmentData) return

    // Hide the tab bar
    toggleTabBar('off')

    async function asyncWrapper() {
      setIsLoading(true)

      // Fetch user's existing preferences and assessment data
      const version = 2
      const result = await getUserAssessmentFormData(version).catch(error => {
        Sentry.captureException(error)
        console.error('Error fetching user data', error)
        setError(error)
      })

      setIsLoading(false)
      setAssessmentData(result)

      // Log the event
      saveUserEvent(EVENT_CATEGORY_ASSESSMENT, EVENT_ACTION_START)
    }
    asyncWrapper()

    // Set the assessment data
  }, [assessmentData])

  return (
    <Container className='user-assessment-container'>
      {/* Did we receive an error? */}
      {error && (
        <div className='error pb-5'>
          <i className='icon bi-exclamation-circle'></i>{' '}
          <strong>Error: </strong> {' ' + error}
        </div>
      )}

      {showForm ? (
        <>
          {isLoading ? (
            <div className='text-center my-5'>
              <Spinner variant='primary' animation='border' />
              <p>Preparing your assessment...</p>
            </div>
          ) : (
            <AssessmentForm
              formData={assessmentData}
              assessmentCompleteCallback={assessmentCompleteCallback}
            />
          )}
        </>
      ) : (
        <Row className='user-assessment'>
          <Col>
            <img
              className='logomark'
              src={RembleLogomark}
              alt='Remble'
              width='25'
              fetchpriority='high'
            />
            <h1 className='title mt-3'>
              Let’s get you the answers and solutions you need!
            </h1>
            <p>
              We understand that you want proven and effective solutions to help
              you overcome your challenges and difficulties and attain a happier
              and healthier you.
            </p>
            <p>
              Help us personalize our approach to you by completing this quick
              assessment.{' '}
            </p>
            <p>Ready to get started? </p>
            <Button
              className='mt-5 w-100'
              variant='primary'
              size='lg'
              onClick={() => setShowForm(true)}
            >
              Ready
            </Button>
          </Col>
        </Row>
      )}
    </Container>
  )
}
